import type { RoutePlan, RouteVisibility } from '@motogo/shared';
import type { FeatureCollection } from 'geojson';
import { createContext, useContext, useMemo, useRef, useState, type FC, type MutableRefObject, type ReactNode } from 'react';

import { shortPlaceName, type GeocodeResult, type GravelMode, type LatLon, type PlannedRoute, type RouteMode } from '@/lib/routing';
import type { SaveRouteInput } from '@/lib/savedRoutes';
import type { GpsPosition } from '@/types/geo';

/**
 * A saved route currently shown on the map — the "current route" the voice tab can push
 * to the channel in one tap. Carries just enough to share it (and to bump a private route
 * to link-visible first, which needs the name/description so the PUT doesn't clobber them).
 */
export interface ActiveRoute {
  id: string;
  name: string;
  description: string | null;
  visibility: RouteVisibility;
}

/** The request snapshot behind a planner-computed route — what a save turns into the plan recipe. */
export interface PlanRequestSnapshot {
  from: LatLon;
  roundTrip: boolean;
  mode: RouteMode;
  gravel: GravelMode;
  destination: GeocodeResult | null;
  distanceKm: number;
  headingDegrees?: number;
  seed?: number;
}

/**
 * Router state handed to /route by "Edit" on a roundTrip-plan saved route (the editor
 * can't reproduce GraphHopper's round_trip algorithm, so the planner is the re-edit
 * surface). Prefills the controls and switches the save flow to updating that route.
 */
export interface PlannerPrefill {
  editRouteId: string;
  name: string;
  description: string | null;
  mode: RouteMode;
  gravel: GravelMode;
  distanceKm: number;
  headingDegrees?: number;
}

/**
 * The *unsaved* route currently on the map — a planner result, a loaded GPX, or a route
 * another rider pushed inline through the channel. Lives here rather than in the planner
 * sheet so dismissing the sheet doesn't lose it: reopening the planner finds it populated
 * for adjustments, and the voice tab can share it (saving it first, or thinned inline).
 * Relationship to `activeRoute` (the saved route on the map): when both are set — a
 * loaded route was just saved — `activeRoute` wins for sharing/bookmark decisions, and
 * `loadedRoute` still describes the geometry. Showing a *different* saved route clears
 * `loadedRoute` (and vice versa: planning/loading clears `activeRoute`).
 */
export type LoadedRoute =
  | { kind: 'planned'; name: string; route: PlannedRoute; request: PlanRequestSnapshot }
  | {
      kind: 'gpx';
      name: string;
      coordinates: [number, number][];
      distanceMeters: number;
      /** Track plan built from the file's `<wpt>` vias, when it had any — see buildTrackPlan. */
      plan?: RoutePlan | null;
    }
  | {
      kind: 'shared';
      name: string;
      fromName: string;
      coordinates: [number, number][];
      distanceMeters: number;
      durationSeconds: number | null;
    };

/** The loaded route's full geometry as [lon, lat] pairs (planned extracts its LineString). */
export function loadedRouteCoordinates(loaded: LoadedRoute): [number, number][] {
  if (loaded.kind !== 'planned') return loaded.coordinates;
  const line = loaded.route.geojson.features.find((f) => f.geometry.type === 'LineString');
  return line && line.geometry.type === 'LineString' ? (line.geometry.coordinates as [number, number][]) : [];
}

/** Estimated riding time in seconds; null when unknowable (GPX import). */
export function loadedRouteDurationSeconds(loaded: LoadedRoute): number | null {
  if (loaded.kind === 'planned') return loaded.route.durationMillis / 1000;
  return loaded.kind === 'shared' ? loaded.durationSeconds : null;
}

export function loadedRouteDistanceMeters(loaded: LoadedRoute): number {
  return loaded.kind === 'planned' ? loaded.route.distanceMeters : loaded.distanceMeters;
}

/**
 * What saving the loaded route persists — shared by the planner's bookmark save and the
 * voice tab's "Save & send". Planner results carry their request snapshot as the plan
 * recipe (keeping them re-editable); GPX and inline-received routes save geometry-only
 * under the closest source, 'gpx'. Null when the geometry is degenerate.
 */
export function buildLoadedRouteSaveInput(loaded: LoadedRoute, name: string): SaveRouteInput | null {
  const id = crypto.randomUUID();
  const coordinates = loadedRouteCoordinates(loaded);
  if (coordinates.length < 2) return null;

  if (loaded.kind !== 'planned') {
    return {
      id,
      name,
      source: 'gpx',
      coordinates,
      durationSeconds: loadedRouteDurationSeconds(loaded),
      // A GPX with via waypoints carries a track plan so the saved route stays editable
      // with those waypoints; shared/inline routes are geometry-only.
      plan: loaded.kind === 'gpx' ? (loaded.plan ?? null) : null,
    };
  }

  const request = loaded.request;
  const plan: RoutePlan = request.roundTrip
    ? {
        version: 1,
        kind: 'roundTrip',
        start: { lat: request.from.lat, lon: request.from.lon },
        distanceKm: request.distanceKm,
        headingDegrees: request.headingDegrees,
        seed: request.seed ?? 0,
        mode: request.mode,
        gravel: request.gravel,
      }
    : {
        version: 1,
        kind: 'waypoints',
        waypoints: [
          { lat: request.from.lat, lon: request.from.lon },
          {
            lat: request.destination!.lat,
            lon: request.destination!.lon,
            name: shortPlaceName(request.destination!.name),
          },
        ],
        segments: [{ mode: request.mode, gravel: request.gravel, endIndex: coordinates.length - 1 }],
      };

  return {
    id,
    name,
    source: 'planner',
    coordinates,
    durationSeconds: loaded.route.durationMillis / 1000,
    plan,
  };
}

/**
 * Bridges the route planner (rendered in the router Outlet at /route) and the
 * persistent map — same data-down pattern as ride review. The planner computes or
 * parses a route and sets it here; `MapView` reacts (feed SOURCES.ROUTE, fitBounds,
 * prefetch cameras). MapView also mirrors the latest GPS fix into `positionRef` so the
 * planner has a start point without spinning up a second watchPosition — a ref, not
 * state, so per-fix updates don't re-render consumers.
 */
interface RoutePlanValue {
  /** Route to draw on the map (computed, or a parsed GPX), or null for none. */
  plannedRoute: FeatureCollection | null;
  setPlannedRoute: (route: FeatureCollection | null) => void;
  /** Latest GPS fix, written by MapView each tick. Read, never write, elsewhere. */
  positionRef: MutableRefObject<GpsPosition | null>;
  /** The saved route on the map right now (set when one is shown), or null for a plan/GPX/none. */
  activeRoute: ActiveRoute | null;
  setActiveRoute: (route: ActiveRoute | null) => void;
  /** The unsaved route on the map right now (planner/GPX/inline share), or null. */
  loadedRoute: LoadedRoute | null;
  setLoadedRoute: (route: LoadedRoute | null) => void;
  /** Set while the planner is re-editing a saved roundTrip route (survives sheet close). */
  editTarget: PlannerPrefill | null;
  setEditTarget: (target: PlannerPrefill | null) => void;
}

const RoutePlanContext = createContext<RoutePlanValue | null>(null);

export const RoutePlanProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [plannedRoute, setPlannedRoute] = useState<FeatureCollection | null>(null);
  const [activeRoute, setActiveRoute] = useState<ActiveRoute | null>(null);
  const [loadedRoute, setLoadedRoute] = useState<LoadedRoute | null>(null);
  const [editTarget, setEditTarget] = useState<PlannerPrefill | null>(null);
  const positionRef = useRef<GpsPosition | null>(null);

  const value = useMemo<RoutePlanValue>(
    () => ({
      plannedRoute,
      setPlannedRoute,
      positionRef,
      activeRoute,
      setActiveRoute,
      loadedRoute,
      setLoadedRoute,
      editTarget,
      setEditTarget,
    }),
    [plannedRoute, activeRoute, loadedRoute, editTarget],
  );

  return <RoutePlanContext.Provider value={value}>{children}</RoutePlanContext.Provider>;
};

export function useRoutePlan(): RoutePlanValue {
  const ctx = useContext(RoutePlanContext);
  if (!ctx) {
    throw new Error('useRoutePlan must be used within a RoutePlanProvider');
  }
  return ctx;
}
